import type { User } from '@/types/directory/User';

import i18n from '@/plugins/i18n';

type NamedUser = Pick<User, 'firstName' | 'lastName'>;

/** A bracketed part of a name, closed or not: a label such as "(Support User)", not a name. */
const BRACKETED_LABEL = /\([^)]*\)?/g;

/** First and last initials of a directory user, or of a full name the API already assembled. */
export const getInitials = (user: NamedUser | string): string => {
    const fullname = typeof user === 'string' ? user : `${user.firstName} ${user.lastName}`;
    const names = fullname.replace(BRACKETED_LABEL, ' ').trim().split(/\s+/);

    const firstInitial = names[0].charAt(0).toUpperCase();
    const lastInitial = names.length > 1 ? names[names.length - 1].charAt(0).toUpperCase() : '';

    return firstInitial + lastInitial;
};

export const getFullName = (user?: NamedUser): string => {
    if (!user) {
        return i18n.t('common.anonymous');
    }

    return `${user.firstName} ${user.lastName}`;
};

let hasWarnedAboutPictureBaseUrl = false;

/**
 * A missing base URL is otherwise invisible: avatars fall back to initials, which looks deliberate,
 * so nothing would point at the configuration. Warned once, and only while developing.
 */
const warnAboutMissingPictureBaseUrl = (): void => {
    if (!import.meta.env.DEV || hasWarnedAboutPictureBaseUrl) {
        return;
    }

    hasWarnedAboutPictureBaseUrl = true;
    console.warn(
        'VITE_PROFILE_PICTURE_BASE_URL is not set: profile pictures stored as a relative path ' +
            'cannot be resolved, so avatars show initials instead. Copy `.env` from the main ' +
            'repository into this worktree and restart the dev server.',
    );
};

/**
 * Where an avatar loads a person's picture from, or an empty string when there is none to resolve —
 * `Avatar` then renders their initials.
 *
 * There is deliberately **no fallback host**. One used to point at the demo environment, so a
 * worktree started without its `.env` showed *other people's faces* in place of the real ones, with
 * no error and no warning. No picture is better than the wrong picture.
 *
 * The variable is read here rather than at module load so a test can exercise both cases, and it is
 * spelled out in full because Vite substitutes `import.meta.env.VITE_*` textually at build time — an
 * indexed access would silently resolve to `undefined` in a production build.
 */
export const getProfilePictureUrl = (profilePictureUrl?: string | null): string => {
    if (!profilePictureUrl) {
        return '';
    }

    const isAbsoluteUrl =
        profilePictureUrl.startsWith('http://') || profilePictureUrl.startsWith('https://');
    if (isAbsoluteUrl) {
        return profilePictureUrl;
    }

    const baseUrl = import.meta.env.VITE_PROFILE_PICTURE_BASE_URL;
    if (!baseUrl) {
        warnAboutMissingPictureBaseUrl();
        return '';
    }

    return `${baseUrl}/${profilePictureUrl}`;
};
